import React from 'react';
import { Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { ReportData } from '../../types/report.types';
import { useSectionPages } from '../SectionPageContext';

interface VulnerabilityRatingDefinitionsPageProps {
  reportData: ReportData;
  sectionId?: string;
}

const styles = StyleSheet.create({
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 0,
    right: 0,
    fontSize: 10,
    color: 'grey',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  footerLeft: {
    textAlign: 'left',
    flex: 1,
  },
  footerRight: {
    textAlign: 'right',
    flex: 1,
  },
});

const VulnerabilityRatingDefinitionsPage: React.FC<VulnerabilityRatingDefinitionsPageProps> = ({ reportData, sectionId }) => {
  const { updateSectionPage } = useSectionPages();
  return (
    <Page size="A4" id={sectionId} style={{
      flexDirection: 'column',
      backgroundColor: '#ffffff',
      padding: '20mm 15mm',
      fontFamily: 'Helvetica',
      fontSize: 12,
    }}>
      <View style={{ flex: 1 }}>
        <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#1e40af', marginBottom: 10, lineHeight: 1.3, letterSpacing: 0.3 }}>
          VULNERABILITY RATING DEFINITIONS
        </Text>
        <Text style={{ fontSize: 11, marginBottom: 16, color: '#64748b', lineHeight: 1.5 }}>
          The following table provides definitions for vulnerability severity levels used throughout this assessment:
        </Text>

        {/* Professional Sleek Table */}
        <View
          style={{
            borderWidth: 1,
            borderColor: '#cbd5e1',
            borderRadius: 8,
            overflow: 'hidden',
            marginBottom: 16,
          }}
        >
          {/* Table Header */}
          <View
            style={{
              flexDirection: 'row',
              backgroundColor: '#1e40af',
              borderBottomWidth: 2,
              borderBottomColor: '#1e3a8a',
            }}
          >
            <Text
              style={{
                width: '25%',
                paddingVertical: 12,
                paddingHorizontal: 14,
                fontWeight: 'bold',
                fontSize: 11,
                color: '#ffffff',
                letterSpacing: 0.5,
              }}
            >
              SEVERITY
            </Text>
            <Text
              style={{
                width: '75%',
                paddingVertical: 12,
                paddingHorizontal: 14,
                fontWeight: 'bold',
                fontSize: 11,
                color: '#ffffff',
                letterSpacing: 0.5,
              }}
            >
              DESCRIPTION
            </Text>
          </View>

          {/* Table Rows */}
          {[
            {
              label: 'Critical',
              color: '#dc2626',
              bgColor: '#ffffff',
              description:
                reportData.vulnerability_ratings?.critical ||
                'Exploitation is straightforward and usually results in system-level compromise. It is advised to form a plan of action and patch immediately.',
            },
            {
              label: 'High',
              color: '#ea580c',
              bgColor: '#fafafa',
              description:
                reportData.vulnerability_ratings?.high ||
                'Exploitation is more difficult but could cause elevated privileges and potentially a loss of data or downtime. It is advised to form a plan of action and patch as soon as possible.',
            },
            {
              label: 'Medium',
              color: '#eab308',
              bgColor: '#ffffff',
              description:
                reportData.vulnerability_ratings?.medium ||
                'Vulnerabilities exist but are not exploitable or require extra steps such as social engineering. It is advised to form a plan of action and patch after high-priority issues have been resolved.',
            },
            {
              label: 'Low',
              color: '#22c55e',
              bgColor: '#fafafa',
              description:
                reportData.vulnerability_ratings?.low ||
                'Vulnerabilities are non-exploitable but would reduce an organization\'s attack surface. It is advised to form a plan of action and patch during the next maintenance window.',
            },
            {
              label: 'Info',
              color: '#64748b',
              bgColor: '#ffffff',
              description:
                reportData.vulnerability_ratings?.info ||
                'Informational findings do not represent a direct security risk but provide additional context or best practices that can improve the overall security posture.',
            },
          ].map((item, idx, arr) => (
            <View
              key={item.label}
              style={{
                flexDirection: 'row',
                backgroundColor: item.bgColor,
                borderBottomWidth: idx < arr.length - 1 ? 1 : 0,
                borderBottomColor: '#e2e8f0',
                alignItems: 'center',
              }}
            >
              {/* Severity Column */}
              <View
                style={{
                  width: '25%',
                  paddingVertical: 14,
                  paddingHorizontal: 14,
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <View
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: item.color,
                    marginRight: 10,
                  }}
                />
                <Text
                  style={{
                    fontSize: 11,
                    fontWeight: 'bold',
                    color: '#1e293b',
                  }}
                >
                  {item.label}
                </Text>
              </View>

              {/* Description Column */}
              <Text
                style={{
                  width: '75%',
                  paddingVertical: 14,
                  paddingHorizontal: 14,
                  fontSize: 10,
                  color: '#475569',
                  lineHeight: 1.5,
                }}
              >
                {item.description}
              </Text>
            </View>
          ))}
        </View>
        <Text
          style={{
            fontSize: 12,
            color: '#64748b',
            textAlign: 'center',
            marginTop: 12,
            letterSpacing: 1,
            lineHeight: 1.4,
          }}
        >
          Table 3: Vulnerability Rating Definitions
        </Text>
      </View>
      <View style={styles.footer} fixed>
        <Text style={styles.footerLeft}>{reportData.document_number || 'Document Number'}</Text>
        <Text style={styles.footerRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
      </View>
      <Text style={{ display: 'none' }} render={({ pageNumber }) => { updateSectionPage('VulnerabilityRatingDefinitions', pageNumber); return ''; }} fixed />
    </Page>
  );
};

export default VulnerabilityRatingDefinitionsPage;
